import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  TablePagination,
  CircularProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  GetApp as DownloadIcon,
  Search as SearchIcon,
  CalendarToday as CalIcon,
  FilterAltOff as ClearIcon,
} from "@mui/icons-material";
import {
  PurchaseOrdersProvider,
  usePurchaseOrders,
} from "../../contexts/alfaroojfresh/PurchaseOrdersContext";
import * as XLSX from "xlsx";
import STORE_NAMES, { getStoreFromUrl } from "../../utils/STORE_NAMES";
const baseUrl = process.env.REACT_APP_BASE_URL;
// ─────────────────────────────────────────
// Theme  (matches PurchaseOrders light-mode)
// ─────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#f8fafc", paper: "#ffffff" },
    primary: { main: "#3753b3" },
    text: { primary: "#0f172a", secondary: "#64748b" },
  },
  typography: {},
});
// ─────────────────────────────────────────
// Animations
// ─────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
// ─────────────────────────────────────────
// Styled components  (same palette as PurchaseOrders)
// ─────────────────────────────────────────
const Root = styled("div")({
  minHeight: "100vh",
  background: "#f8fafc",
  padding: "40px 48px",
  animation: `${fadeUp} 500ms ease both`,
});
const TitleBlock = styled("div")({
  "& .eyebrow": {
    fontSize: "0.65rem",
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: "#3753b3",
    marginBottom: 6,
  },
  "& h1": {
    margin: 0,
    fontSize: "2rem",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.03em",
    lineHeight: 1,
  },
  "& .sub": { fontSize: "0.75rem", color: "#94a3b8", marginTop: 6 },
});
const ActionBtn = styled(Button)({
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: 8,
  padding: "8px 16px",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  background: "#ffffff",
  transition: "all 0.2s",
  "&:hover": {
    borderColor: "#3753b3",
    color: "#3753b3",
    background: "rgba(245,158,11,0.05)",
  },
});
const StatCard = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "14px 18px",
  minWidth: 110,
  "& .val": {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1,
  },
  "& .lbl": {
    fontSize: "0.62rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginTop: 4,
  },
});
const FilterBar = styled("div")({
  display: "flex",
  gap: 12,
  marginBottom: 20,
  alignItems: "center",
  flexWrap: "wrap",
});
const SectionBox = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: "24px 28px",
  marginBottom: 20,
  boxShadow: "0 2px 12px rgba(15,23,42,0.04)",
});
const SectionTitle = styled("div")({
  fontSize: "0.85rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 16,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "& .accent": { color: "#3753b3" },
});
const StyledTableContainer = styled(Paper)({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
  overflow: "hidden",
});
const tableHeadSx = {
  "& .MuiTableCell-head": {
    fontSize: "0.65rem",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: "#94a3b8",
    background: "#f8fafc",
    fontWeight: 600,
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};
const tableBodySx = {
  "& .MuiTableRow-root": {
    transition: "background 0.15s",
    "&:hover": { background: "#fefce8" },
  },
  "& .MuiTableCell-body": {
    fontSize: "0.78rem",
    color: "#475569",
    padding: "12px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};
const RefChip = styled("span")({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 5,
  background: "#3753b3",
  border: "1px solid #09194d",
  color: "whiteSmoke",
  fontSize: "0.72rem",
  letterSpacing: "0.05em",
});
const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.78rem",
    background: "#ffffff",
    borderRadius: 2,
    color: "#0f172a",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#3753b3" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.75rem", color: "#94a3b8" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#3753b3" },
};
const menuPropsSx = {
  PaperProps: {
    sx: {
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 2,
      boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
      "& .MuiMenuItem-root": {
        fontSize: "0.78rem",
        color: "#475569",
        "&:hover": { background: "#fefce8", color: "#d97706" },
        "&.Mui-selected": {
          background: "rgba(245,158,11,0.08)",
          color: "#3753b3",
        },
      },
    },
  },
};
// ─────────────────────────────────────────
// Helper: build row for a PO line
// ─────────────────────────────────────────
function buildLine(po, item, goodsList, suppliersList) {
  const goodObj = goodsList?.find((g) => String(g?._id) === String(item.good));
  const supplier = suppliersList?.find(
    (s) => String(s?._id) === String(item.supplier)
  );
  const qty = Number(item.quantity) || 0;
  const unitPrice = Number(item.price_used || 0);
  const subtotal = qty * unitPrice;
  const tax = Number(item.total_tax_used || 0);
  const total = subtotal + tax;
  return {
    poRef:
      po.order_no ||
      po.invoice_no ||
      (po._id ? `#${String(po?._id).slice(0, 8)}` : "N/A"),
    poId: po?._id,
    date: po.received_date || po.orderDate || null,
    supplierName: supplier?.name || item.supplier || "N/A",
    goodName: goodObj?.name || item.good,
    qty,
    unit: goodObj?.measurement_unit || "N/A",
    unitPrice,
    subtotal,
    tax,
    total,
    storeIndex: po.store,
  };
}
// ─────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────
const PurchasesByUnit = () => {
  const {
    purchaseOrders = [],
    purchaseOrders1 = [],
    purchaseOrders2 = [],
    purchaseOrders3 = [],
    purchaseOrders4 = [],
    purchaseOrders5 = [],
    purchaseOrders6 = [],
    purchaseOrders7 = [],
    purchaseOrders8 = [],
    purchaseOrders9 = [],
    purchaseOrders10 = [],
    purchaseOrders11 = [],
    purchaseOrders12 = [],
    purchaseOrders13 = [],
    goods = [],
    suppliers = [],
    loading,
    error,
  } = usePurchaseOrders();
  const endpoint0 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_0;
  const endpoint1 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_1;
  const endpoint2 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_2;
  const endpoint3 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_3;
  const endpoint4 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_4;
  const endpoint5 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_5;
  const endpoint6 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_6;
  const endpoint7 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_7;
  const endpoint8 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_8;
  const endpoint9 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_9;
  const endpoint10 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_10;
  const endpoint11 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_11;
  const endpoint12 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_12;
  const endpoint13 = process.env.REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_13;
  const getStoreFromUrl = (url) => {
    try {
      const params = new URL(url).searchParams;
      return params.get("0_store");
    } catch (err) {
      console.error("Invalid URL:", err);
      return null;
    }
  };
  const storeId0 = getStoreFromUrl(endpoint0);
  const storeId1 = getStoreFromUrl(endpoint1);
  const storeId2 = getStoreFromUrl(endpoint2);
  const storeId3 = getStoreFromUrl(endpoint3);
  const storeId4 = getStoreFromUrl(endpoint4);
  const storeId5 = getStoreFromUrl(endpoint5);
  const storeId6 = getStoreFromUrl(endpoint6);
  const storeId7 = getStoreFromUrl(endpoint7);
  const storeId8 = getStoreFromUrl(endpoint8);
  const storeId9 = getStoreFromUrl(endpoint9);
  const storeId10 = getStoreFromUrl(endpoint10);
  const storeId11 = getStoreFromUrl(endpoint11);
  const storeId12 = getStoreFromUrl(endpoint12);
  const storeId13 = getStoreFromUrl(endpoint13);
  const storeName0 = STORE_NAMES[storeId0] || "Unknown Store";
  const storeName1 = STORE_NAMES[storeId1] || "Unknown Store";
  const storeName2 = STORE_NAMES[storeId2] || "Unknown Store";
  const storeName3 = STORE_NAMES[storeId3] || "Unknown Store";
  const storeName4 = STORE_NAMES[storeId4] || "Unknown Store";
  const storeName5 = STORE_NAMES[storeId5] || "Unknown Store";
  const storeName6 = STORE_NAMES[storeId6] || "Unknown Store";
  const storeName7 = STORE_NAMES[storeId7] || "Unknown Store";
  const allPOs = useMemo(
    () => [
      ...(purchaseOrders || []),
      ...(purchaseOrders1 || []),
      ...(purchaseOrders2 || []),
      ...(purchaseOrders3 || []),
      ...(purchaseOrders4 || []),
      ...(purchaseOrders5 || []),
      ...(purchaseOrders6 || []),
      ...(purchaseOrders7 || []),
      ...(purchaseOrders8 || []),
      ...(purchaseOrders9 || []),
      ...(purchaseOrders10 || []),
      ...(purchaseOrders11 || []),
      ...(purchaseOrders12 || []),
      ...(purchaseOrders13 || []),
    ],
    [
      purchaseOrders,
      purchaseOrders1,
      purchaseOrders2,
      purchaseOrders3,
      purchaseOrders4,
      purchaseOrders5,
      purchaseOrders6,
      purchaseOrders7,
      purchaseOrders8,
      purchaseOrders9,
      purchaseOrders10,
      purchaseOrders11,
      purchaseOrders12,
      purchaseOrders13,
    ]
  );
  // collect unique measurement units
  const allUnits = useMemo(() => {
    const units = new Set(goods.map((g) => g.measurement_unit).filter(Boolean));
    return Array.from(units).sort();
  }, [goods]);
  const allStoreNames = useMemo(() => Object.values(STORE_NAMES), []);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [search, setSearch] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const getStoreNameForPO = (po) => {
    const names = [
      "Al Farooj Fresh - Abu Hail Dubai",
      "Al Farooj Fresh - Warehouse",
      "Al Farooj Fresh - Central Kitchen",
      "Al Farooj Fresh - Al Barsha Mall Dubai",
      "Al Farooj Fresh - King Faisal Sharjah",
      "Al Farooj Fresh - Manaseer Al Ain",
      "Al Farooj Fresh - Tawam Al Ain",
      "AL Farooj Fresh - Mirdiff Way Food Truck",
      "Al Farooj Fresh - LIWA",
      "Al Farooj Fresh - Catering ( Al Wasl Club )",
      "Al Farooj Fresh - Ajman",
      "Al Farooj Fresh - Kiosk",
      "Al Farooj Fresh - Al Hilli Al Ain",
      "Al Farooj Fresh - Ajman Food Truck",
    ];
    return names[po.storeIndex] || "Unknown Store";
  };
  const rows = useMemo(() => {
    if (!selectedUnit) return [];
    const matched = [];
    allPOs.forEach((po) => {
      if (!Array.isArray(po.po_inv_items)) return;
      po.po_inv_items.forEach((item) => {
        const goodObj = goods.find(
          (g) => String(g?._id) === String(item.good)
        );
        if (goodObj?.measurement_unit === selectedUnit) {
          const line = buildLine(po, item, goods, suppliers);
          line.storeName = getStoreNameForPO(po);
          matched.push(line);
        }
      });
    });
    // apply store filter
    const byStore = storeFilter
      ? matched.filter((r) => r.storeName === storeFilter)
      : matched;
    // apply date filters
    const fromDate = filterFrom ? new Date(filterFrom) : null;
    const toDate = filterTo ? new Date(filterTo) : null;
    const byDate = byStore.filter((r) => {
      if (!r.date) return true;
      const d = new Date(r.date);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    });
    // apply search
    const q = (search || "").trim().toLowerCase();
    const bySearch = q
      ? byDate.filter(
          (r) =>
            (r.poRef || "").toString().toLowerCase().includes(q) ||
            (r.supplierName || "").toString().toLowerCase().includes(q) ||
            (r.goodName || "").toString().toLowerCase().includes(q) ||
            (r.storeName || "").toString().toLowerCase().includes(q)
        )
      : byDate;
    // sort newest first
    bySearch.sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
    return bySearch;
  }, [
    selectedUnit,
    allPOs,
    goods,
    suppliers,
    storeFilter,
    filterFrom,
    filterTo,
    search,
  ]);
  const footerTotals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.qty += r.qty;
        acc.subtotal += r.subtotal;
        acc.tax += r.tax;
        acc.total += r.total;
        return acc;
      },
      { qty: 0, subtotal: 0, tax: 0, total: 0 }
    );
  }, [rows]);
  const pageRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  // Has any refine filter active?
  const hasFilters = storeFilter || filterFrom || filterTo || search;
  const exportToExcel = () => {
    if (!selectedUnit) return;
    const sheetRows = rows.map((r) => ({
      "PO Ref": r.poRef,
      Date: r.date,
      Store: r.storeName,
      Supplier: r.supplierName,
      Good: r.goodName,
      Quantity: r.qty,
      Unit: r.unit,
      "Unit Price": r.unitPrice.toFixed(2),
      Subtotal: r.subtotal.toFixed(2),
      Tax: r.tax.toFixed(2),
      Total: r.total.toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(sheetRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchases by Unit");
    XLSX.writeFile(
      wb,
      `Purchases_by_Unit_${selectedUnit}_filtered_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };
  // ── Loading / Error states ──
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ background: "#f8fafc" }}
      >
        <CircularProgress sx={{ color: "#3753b3" }} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ background: "#f8fafc" }}
      >
        <Typography sx={{ color: "#ef4444" }}>
          Error: {String(error)}
        </Typography>
      </Box>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <Root>
        {/* ── Page header ── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <TitleBlock>
            <div className="eyebrow">Procurement</div>
            <h1>Purchases by Measurement Unit</h1>
            <div className="sub">
              Select a measurement unit to list all goods purchased with it.
            </div>
          </TitleBlock>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <StatCard>
              <div className="val">{rows.length}</div>
              <div className="lbl">Results</div>
            </StatCard>
            <StatCard>
              <div className="val">AED {footerTotals.total.toFixed(0)}</div>
              <div className="lbl">Total Value</div>
            </StatCard>
            <ActionBtn
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={!selectedUnit || rows.length === 0}
            >
              Excel
            </ActionBtn>
          </Box>
        </Box>
        {/* ── Selection Filter ── */}
        <SectionBox>
          <SectionTitle>
            <span className="accent">◈</span> Selection Filter
          </SectionTitle>
          <FilterBar>
            <TextField
              label="Measurement Unit"
              select
              size="small"
              value={selectedUnit}
              onChange={(e) => {
                setSelectedUnit(e.target.value);
                setPage(0);
              }}
              sx={{ ...inputSx, minWidth: 280 }}
              SelectProps={{ MenuProps: menuPropsSx }}
            >
              <MenuItem value="">-- Select Unit --</MenuItem>
              {allUnits.map((u) => (
                <MenuItem key={u} value={u}>
                  {u}
                </MenuItem>
              ))}
            </TextField>
          </FilterBar>
        </SectionBox>
        {/* ── Refine Filters ── */}
        <SectionBox>
          <SectionTitle>
            <span className="accent">◈</span> Refine Results
          </SectionTitle>
          <FilterBar>
            <TextField
              placeholder="Search…"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, flex: 1, minWidth: 220 }}
            />
            <TextField
              label="Store"
              select
              size="small"
              value={storeFilter}
              onChange={(e) => {
                setStoreFilter(e.target.value);
                setPage(0);
              }}
              sx={{ ...inputSx, minWidth: 260 }}
              SelectProps={{ MenuProps: menuPropsSx }}
            >
              <MenuItem value="">All stores</MenuItem>
              {allStoreNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="From"
              variant="outlined"
              size="small"
              type="date"
              value={filterFrom}
              onChange={(e) => {
                setFilterFrom(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, minWidth: 160 }}
            />
            <TextField
              label="To"
              variant="outlined"
              size="small"
              type="date"
              value={filterTo}
              onChange={(e) => {
                setFilterTo(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, minWidth: 160 }}
            />
            {hasFilters && (
              <ActionBtn
                startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
                onClick={() => {
                  setStoreFilter("");
                  setSearch("");
                  setFilterFrom("");
                  setFilterTo("");
                }}
                sx={{
                  borderColor: "#fecaca",
                  color: "#ef4444",
                  "&:hover": {
                    borderColor: "#ef4444",
                    background: "rgba(239,68,68,0.05)",
                  },
                }}
              >
                Clear
              </ActionBtn>
            )}
          </FilterBar>
        </SectionBox>
        {/* ── Data Table ── */}
        <SectionBox>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <SectionTitle sx={{ mb: 0 }}>
              <span className="accent">◈</span> Purchase Orders
              <span
                style={{
                  color: "#94a3b8",
                  fontWeight: 400,
                  fontSize: "0.72rem",
                }}
              >
                — {rows.length} records
              </span>
            </SectionTitle>
            <Box display="flex" gap={3}>
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Total Qty:{" "}
                <strong style={{ color: "#0f172a" }}>
                  {footerTotals.qty}
                </strong>
              </span>
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Total Value:{" "}
                <strong style={{ color: "#3753b3" }}>
                  AED {footerTotals.total.toFixed(2)}
                </strong>
              </span>
            </Box>
          </Box>
          <TableContainer component={StyledTableContainer}>
            <Table sx={{ ...tableHeadSx, ...tableBodySx }}>
              <TableHead>
                <TableRow>
                  <TableCell>PO Ref</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Good</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((r, idx) => (
                  <TableRow key={`${r?.poId}-${idx}`}>
                    <TableCell>
                      <RefChip>{r.poRef}</RefChip>
                    </TableCell>
                    <TableCell>{r.date || "—"}</TableCell>
                    <TableCell>
                      <span style={{ color: "#0f172a", fontWeight: 600 }}>
                        {r.storeName}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span style={{ color: "#0f172a", fontWeight: 600 }}>
                        {r.supplierName}
                      </span>
                    </TableCell>
                    <TableCell>{r.goodName}</TableCell>
                    <TableCell align="right">{r.qty}</TableCell>
                    <TableCell>
                      <span style={{ color: "#cbd5e1", fontSize: "0.7rem" }}>
                        {r.unit}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      {r.unitPrice.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      <span style={{ color: "#3753b3", fontWeight: 600 }}>
                        {r.subtotal.toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell align="right">{r.tax.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <span style={{ fontWeight: 700, color: "#0f172a" }}>
                        {r.total.toFixed(2)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                {pageRows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      align="center"
                      sx={{
                        color: "#94a3b8 !important",
                        padding: "36px !important",
                        fontStyle: "italic",
                      }}
                    >
                      {selectedUnit
                        ? "No purchases found for this unit (with current filters)."
                        : "Select a measurement unit to start."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ background: "#f8fafc" }}>
                  <TableCell
                    colSpan={5}
                    sx={{
                      fontWeight: 700,
                      textAlign: "right",
                      color: "#0f172a !important",
                      fontSize: "0.75rem !important",
                    }}
                  >
                    Totals:
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {footerTotals.qty}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: "#3753b3 !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {footerTotals.subtotal.toFixed(2)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {footerTotals.tax.toFixed(2)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {footerTotals.total.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              color: "#94a3b8",
              fontSize: "0.72rem",
              "& .MuiSelect-icon": { color: "#94a3b8" },
              "& .MuiIconButton-root": {
                color: "#94a3b8",
                "&:hover": { color: "#3753b3" },
              },
              "& .Mui-disabled": { color: "#e2e8f0 !important" },
            }}
          />
        </SectionBox>
      </Root>
    </ThemeProvider>
  );
};
const PurchasesByUnitPage = () => (
  <PurchaseOrdersProvider
    endpoint0={`${baseUrl}/api/api-farooj-index/combined-0`}
    endpoint1={`${baseUrl}/api/api-farooj-index/combined-1`}
    endpoint2={`${baseUrl}/api/api-farooj-index/combined-2`}
    endpoint3={`${baseUrl}/api/api-farooj-index/combined-3`}
    endpoint4={`${baseUrl}/api/api-farooj-index/combined-4`}
    endpoint5={`${baseUrl}/api/api-farooj-index/combined-5`}
    endpoint6={`${baseUrl}/api/api-farooj-index/combined-6`}
    endpoint7={`${baseUrl}/api/api-farooj-index/combined-7`}
    endpoint8={`${baseUrl}/api/api-farooj-index/combined-8`}
    endpoint9={`${baseUrl}/api/api-farooj-index/combined-9`}
    endpoint10={`${baseUrl}/api/api-farooj-index/combined-10`}
    endpoint11={`${baseUrl}/api/api-farooj-index/combined-11`}
    endpoint12={`${baseUrl}/api/api-farooj-index/combined-12`}
    endpoint13={`${baseUrl}/api/api-farooj-index/combined-13`}
  >
    <PurchasesByUnit />
  </PurchaseOrdersProvider>
);
export default PurchasesByUnitPage;